<script>
import axios from 'axios'
import moment from 'moment'
import isEmpty from 'lodash/isEmpty'
import api from '@/helpers/api'
import { v4 as uuidv4 } from 'uuid'
import { required, integer, minValue, maxValue } from 'vuelidate/lib/validators'

const validatSize = (size, max) => {
  if (!max) return true
  // max in MB, converted to Bytes
  const maxBytes = max * 1000000
  return size <= maxBytes
}

export default {
  name: 'multi_file_upload',
  data: () => ({
    progress: 0,
    isUploading: false,
    file: null,
    files: [],
    upload_files: [],
    upload_url: null,
    file_id: null,
    errorMsg: null,
    isUploaded: false
  }),
  props: {
    manualUpload: { type: Boolean, default: false },
    directory: { type: String, default: 'uploads' },
    allowedExtensions: { type: Array, default: () => [] },
    maxSizeMb: { type: Number, default: 16 },
    maxFiles: { type: Number, default: 5 },
    value: { type: Array, default: () => [] }
  },
  watch: {
    value: {
      async handler (v) {
        if (v) {
          let { items } = await api.files.fetchPage({
            page: 1,
            pageSize: -1,
            filters: [{ id: { _in: v.map(f => f.file_id || f.id) } }]
          })

          this.files = items.map(file => {
            const { id, file_name, file_size, file_content_type, uploaded_at } =
              file
            return {
              uid: id,
              id,
              file_id: id,
              data: new File([], file_name, {
                size: file_size,
                type: file_content_type,
                lastModified: uploaded_at
              }),
              uploaded_at,
              is_uploaded: !!uploaded_at,
              label: `${file_name} (${this.$options.filters.prettyBytes(
                file_size
              )})`,
              errors: {}
            }
          })
        }
      },
      immediate: true
    }
  },

  validations () {
    let maxSize = this.maxSizeMb * 1048576 // 1048576b = 1MB
    return {
      file: {
        size: {
          required,
          maxValue: maxValue(maxSize)
        },
        name: {
          required,
          extension (v) {
            if (!v || this.allowedExtensions.length === 0) return true
            let extension = v.replace(/^.*\./, '.')
            return this.allowedExtensions.includes(extension)
          }
        }
      }
    }
  },
  computed: {
    inputValue () {
      return this.file
        ? `${this.file.name} (${this.$options.filters.prettyBytes(
            this.file.size
          )})`
        : null
    },
    isLoading () {
      return false
    },
    fileSelected () {
      return this.files.length > 0
    },
    maxFilesSelected () {
      return this.files.length >= this.maxFiles
    },
    uploadDisabled () {
      return this.maxFilesSelected ? 'disabled' : this.$attrs.disabled
    },
    accept () {
      return this.allowedExtensions.join(',')
    },
    description () {
      if (this.$v.file.$error) return null
      let s = []
      if (this.maxFiles) s.push(`Max ${this.maxFiles} files per order`)
      if (this.maxSizeMb) s.push(`Max. file size is ${this.maxSizeMb}MB`)
      if (this.allowedExtensions.length)
        s.push(`You can only upload image or pdf files`)

      return s.join(', ')
    }
  },
  methods: {
    onBrowseFileHandler () {
      if (this.files.length < this.maxFiles) this.$refs['__file__'].click()
    },
    onFileChangedHandler (event) {
      let filteredList = Array.from(event.target.files)

      let take = this.maxFiles - this.files.length
      filteredList = filteredList.slice(0, take)

      this.files = [
        ...this.files,
        ...filteredList.map(f => {
          let errors = {}
          const { size } = f
          let validSize = validatSize(size, this.maxSizeMb)
          if (!validSize) errors['size'] = `Max. ${this.maxSizeMb} MB!`

          return {
            uid: uuidv4(),
            label: `${f.name} (${this.$options.filters.prettyBytes(f.size)})`,
            data: f,
            progress: 0,
            loading: false,
            completed: false,
            errors,
            is_uploaded: false
          }
        })
      ]
    },

    async uploadFilesAsync () {
      return Promise.all(
        this.files.map(async item => {
          return await this.uploadFile(item)
        })
      )
    },

    async uploadFile (file) {
      return new Promise(async resolve => {
        if (file.is_uploaded) return resolve(file)

        // get upload url
        const {
          data: { name, type, size }
        } = file

        let { url, file_id } = await api.files.upload_url({
          file_name: name,
          file_content_type: isEmpty(type) ? 'application/octet-stream' : type,
          file_size: size,
          key: `${this.directory}`
        })

        file = {
          ...file,
          url,
          file_id,
          progress: 0,
          is_uploading: false
        }

        var options = {
          headers: {
            'Content-Type': isEmpty(type) ? 'application/octet-stream' : type,
            'x-amz-acl': 'private'
          },
          onUploadProgress: event => {
            file.progress = Math.round((100 * event.loaded) / event.total)
          }
        }

        try {
          file.is_uploading = true
          await axios.put(file.url, file.data, options)

          await api.files.update({
            id: file.file_id,
            uploaded_at: moment().format()
          })

          file.is_uploading = false
        } catch (error) {
          file.is_uploading = false
          throw new Error(error)
        }

        resolve(file)
      })
    },

    async onStartUploadHandler () {
      if (!this.file || isEmpty(this.upload_url) || isEmpty(this.file_id))
        return Promise.resolve(true)
      const { name, type, size } = this.file
      var options = {
        headers: {
          'Content-Type': isEmpty(type) ? 'application/octet-stream' : type,
          'x-amz-acl': 'private'
        },
        onUploadProgress: event => {
          this.progress = Math.round((100 * event.loaded) / event.total)
        }
      }

      try {
        this.isUploading = true
        await axios.put(this.upload_url, this.file, options)

        await api.files.update({
          id: this.file_id,
          uploaded_at: moment().format()
        })

        this.$emit('input', this.file_id)
        this.isUploading = false
      } catch (error) {
        this.isUploading = false
        throw new Error(error)
      }
    },

    async onRemoveFileHandler (file) {
      this.files = this.files.filter(f => f.uid !== file.uid)
      if (file.id) {
        await api.files.delete({ id: file.id })
        this.$emit('file-removed', file)
      }
    },

    async onClearAllHandler () {
      this.$refs['__file__'].value = null
      if (this.upload_files.length > 0)
        await api.files.deleteAll({
          where: { id: { _in: this.upload_files.map(f => f.file_id) } }
        })
      this.$v.$reset()
      this.files = []
      this.upload_files = []
      this.progress = 0
      this.file_id = null
      this.upload_url = null
      this.errorMsg = null
      this.isUploaded = false

      this.$emit('input', null)
    }
  }
}
</script>
<template>
  <div>
    <input
      type="file"
      class="d-none"
      ref="__file__"
      :accept="accept"
      name="file_input"
      multiple
      v-on:change="onFileChangedHandler"
    />

    <b-row>
      <b-col :cols="12" class="mb-2 pr-0 mr-0 d-flex justify-content-between">
        <b-btn
          variant="primary"
          v-on:click="onBrowseFileHandler"
          :disabled="uploadDisabled"
          ><i class="bx bx-import mr-1"></i>
          {{ $t('select file(s)') }}
        </b-btn>
        <b-btn
          v-if="fileSelected"
          variant="link"
          v-on:click="onClearAllHandler"
          :disabled="$attrs.disabled || (progress < 100 && isUploading)"
          ><i class="far fa-trash-alt text-danger mr-2"></i
          >{{ $t('clear all') }}</b-btn
        >
      </b-col>
    </b-row>
    <b-row>
      <b-col class="mb-2">
        <small class="d-block">{{ description }}</small>
      </b-col>
    </b-row>
    <b-row style="max-height: 300px; overflow-y: auto">
      <b-col v-for="file in files" :key="file.uid" class="py-1" :cols="12">
        <div>
          <div class="w-100">
            <span>
              {{ file.data.name }}
            </span>
            <small class="mx-2">
              {{ file.data.size | prettyBytes }}
              <span class="ml-1 text-danger" v-if="file.errors['size']">
                ({{ file.errors['size'] }}, will not be uploaded)
              </span>
            </small>

            <small class="d-block" v-if="file.uploaded_at">
              Uploaded
              {{ file.uploaded_at | moment('from', 'now') }}
            </small>
            <small class="d-block" v-else>
              Last Modified
              {{ file.data.lastModifiedDate | moment('from', 'now') }}
            </small>
            <b-progress
              v-if="file.loading"
              :value="file.progress"
              :max="100"
              :show-progress="false"
              :animated="!file.completed"
              :rounded="false"
              height="3px"
              variant="success"
              class="w-100 mt-1"
            ></b-progress>
          </div>
          <b-btn
            variant="link"
            size="lg"
            class="m-1 p-1"
            v-on:click="onRemoveFileHandler(file)"
            style="position: absolute; right: 0; top: 0"
          >
            <i class="bx bx-trash text-danger"></i>
          </b-btn>
          <i class="bx bx-loader-alt fa-spin" v-if="file.is_uploading" />
        </div>
      </b-col>
    </b-row>
  </div>
</template>
